import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import useSWR from "swr";

import { Icon, type IconName } from "../components/Icon";
import { CATALOG_RAISE_KEY, RaiseTicketLink } from "../components/RaiseTicketLink";
import { api, type FormCatalogueItem, type FormIconColor, type Me } from "../services/api";

const ICON_COLORS: FormIconColor[] = ["violet", "green", "orange", "red", "blue"];

/**
 * Pick a stable accent colour for a catalogue card. FormCatalogueItem carries
 * no icon_color, so we hash the category (falling back to the title) to keep
 * the choice deterministic across renders while still looking varied.
 */
function iconColorFor(item: FormCatalogueItem): FormIconColor {
  const seed = (item.category?.trim() || item.title || item.slug || "").trim();
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  }
  return ICON_COLORS[Math.abs(hash) % ICON_COLORS.length];
}

/**
 * Pick a glyph for a service card. The catalogue feed carries no icon name, so
 * we map common service categories/titles to a sensible glyph (a document is
 * the neutral fallback). Keyword-based so admin-created categories still land
 * on something meaningful.
 */
function iconForItem(item: FormCatalogueItem): IconName {
  const hay = `${item.category ?? ""} ${item.title} ${item.slug}`.toLowerCase();
  if (/offboard|leaver|depart/.test(hay)) return "userMinus";
  if (/onboard|new starter|new-starter|joiner|new user/.test(hay)) return "userPlus";
  if (/access|permission|account|licen[cs]e/.test(hay)) return "key";
  if (/support|incident|help|issue|fault/.test(hay)) return "lifeBuoy";
  if (/hardware|device|laptop|equipment/.test(hay)) return "monitor";
  if (/security|mfa|phish/.test(hay)) return "shield";
  if (/mail|email|software/.test(hay)) return "mail";
  return "doc";
}

/**
 * Service Catalog (Brief v1.2 §7.1), in the refined "Online Forms" look
 * (design handoff §5.1). The published Forms feed (api.forms.list), grouped
 * into category sections and presented as service cards: a coloured icon, the
 * category, title, description, and a request-meta footer (estimated time +
 * an approval/instant tag). A pill search and category chip-row filter the
 * grid client-side; a dashed bottom CTA deflects to a general ticket.
 *
 * The Service-Hub sub-navigation is the existing vertical sub-rail
 * (ServiceHubLayout) — kept deliberately for the space it gives this page, so
 * no horizontal tab-bar is rendered here.
 *
 * Data logic (icon colour hash, glyph mapping, est_time / has_approval) is
 * unchanged — those values are derived server-side, never invented.
 */
export function ServiceCatalog() {
  const { data: me } = useSWR<Me>("/api/me", () => api.me());
  const { data, error, isLoading } = useSWR<FormCatalogueItem[]>("/api/forms", () =>
    api.forms.list(),
  );

  const [query, setQuery] = useState("");
  const [activeCat, setActiveCat] = useState<string>("All");

  const items = useMemo(() => data ?? [], [data]);

  // Distinct categories, alphabetical with "Uncategorised" last — drives chips.
  const categories = useMemo(() => {
    const set = new Set<string>();
    for (const f of items) set.add(f.category?.trim() || "Uncategorised");
    const list = Array.from(set).sort((a, b) => {
      if (a === "Uncategorised") return 1;
      if (b === "Uncategorised") return -1;
      return a.localeCompare(b);
    });
    return ["All", ...list];
  }, [items]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((f) => {
      const cat = f.category?.trim() || "Uncategorised";
      if (activeCat !== "All" && cat !== activeCat) return false;
      if (!q) return true;
      return `${f.title} ${f.description ?? ""} ${cat}`.toLowerCase().includes(q);
    });
  }, [items, query, activeCat]);

  const grouped = useMemo(() => groupByCategory(filtered), [filtered]);

  return (
    <div className="rf-wrap">
      <div className="rf-pagehead">
        <h1 className="rf-h1">Online Forms</h1>
        <p className="rf-sub">
          Submit a structured request and we&apos;ll route it to the right team
          automatically &mdash; with approvals, SLAs and status tracking built in.
        </p>
      </div>

      {isLoading ? (
        <div className="card">Loading&hellip;</div>
      ) : error ? (
        <div className="card form-error">Failed to load services: {String(error)}</div>
      ) : items.length === 0 ? (
        <div className="card empty-state">
          <p>No services available yet.</p>
          <p className="empty-state-blurb">
            Your iQuest admin hasn&apos;t published any services. Check back later.
          </p>
        </div>
      ) : (
        <>
          <div className="rf-catalog-controls">
            <div className="rf-search">
              <Icon name="search" />
              <input
                type="text"
                value={query}
                placeholder="Search forms — e.g. new starter, access, hardware…"
                aria-label="Search forms"
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            <div className="rf-chiprow">
              {categories.map((cat) => (
                <button
                  key={cat}
                  type="button"
                  className={"rf-chip" + (cat === activeCat ? " active" : "")}
                  onClick={() => setActiveCat(cat)}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          {grouped.length === 0 ? (
            <div className="card empty-state">
              <p>No forms match your search.</p>
              <p className="empty-state-blurb">Try a different term or category.</p>
            </div>
          ) : (
            grouped.map(([category, group]) => (
              <section key={category} className="rf-group">
                <div className="rf-group-head">
                  <span className="rf-group-title">{category}</span>
                  <span className="rf-group-rule" />
                  <span className="rf-group-count">
                    {group.length} {group.length === 1 ? "form" : "forms"}
                  </span>
                </div>
                <div className="rf-card-grid">
                  {group.map((f) => (
                    <Link key={f.id} to={`/forms/${f.id}`} className="rf-form-card">
                      <div className="rf-form-card-top">
                        <span className={`rf-card-icon ${iconColorFor(f)}`}>
                          <Icon name={iconForItem(f)} size={22} />
                        </span>
                        <span className="rf-card-arrow">
                          <Icon name="arrowRight" size={16} />
                        </span>
                      </div>
                      <div className="rf-card-cat">{category}</div>
                      <div className="rf-card-title">{f.title}</div>
                      {f.description && <p className="rf-card-desc">{f.description}</p>}
                      <div className="rf-card-foot">
                        {f.est_time && (
                          <span className="rf-meta">
                            <Icon name="clock" size={14} />
                            {f.est_time}
                          </span>
                        )}
                        {f.has_approval ? (
                          <span className="rf-tag approval">
                            <Icon name="shield" size={12} />
                            Approval
                          </span>
                        ) : (
                          <span className="rf-tag instant">
                            <Icon name="check" size={12} />
                            No approval
                          </span>
                        )}
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            ))
          )}

          <div className="rf-bottom-cta">
            <div>
              <div className="t">Can&apos;t find the right form?</div>
              <div className="s">Raise a general IT ticket and our team will pick it up.</div>
            </div>
            <RaiseTicketLink
              className="btn btn-ghost"
              impersonating={!!me?.impersonating}
              returnPath="/services"
              focusKey={CATALOG_RAISE_KEY}
            >
              <Icon name="ticket" size={16} />
              Raise a ticket
            </RaiseTicketLink>
          </div>
        </>
      )}
    </div>
  );
}

function groupByCategory(
  items: FormCatalogueItem[],
): Array<[string, FormCatalogueItem[]]> {
  const buckets = new Map<string, FormCatalogueItem[]>();
  for (const item of items) {
    const key = item.category?.trim() || "Uncategorised";
    const bucket = buckets.get(key) ?? [];
    bucket.push(item);
    buckets.set(key, bucket);
  }
  // Sort categories alphabetically; pin "Uncategorised" to the end.
  return Array.from(buckets.entries()).sort(([a], [b]) => {
    if (a === "Uncategorised") return 1;
    if (b === "Uncategorised") return -1;
    return a.localeCompare(b);
  });
}

export default ServiceCatalog;
